/**
 * Followers Section (Leader Dashboard)
 *
 * 나의 팔로워 관리 섹션 - 신규 디자인 스타일
 * - 팔로워 목록 테이블
 * - 팔로워별 통계 (복사 비율, 할당 금액, PnL, ROI)
 * - 필터링/정렬
 * - 요약 카드
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';

import { getMyFollowers } from '@app/api/copyTrading';
import { useMyPortfolio } from '@app/hooks/copyTrading/useMyPortfolio';
import CSelect, { SelectDropdownGlobalStyle } from '@app/components/Common/CSelect';

import type { CopyFollow } from '../../../../types/copyTrading';

type FollowerWithStats = {
  follow: CopyFollow & {
    followerUsername?: string;
    followerProfileImage?: string;
  };
  stats: {
    totalTrades: number;
    totalProfitShared: number;
    roi: number;
  };
};

interface FollowersSectionProps {
  isMobile?: boolean;
}

export const FollowersSection: React.FC<FollowersSectionProps> = ({ isMobile = true }) => {
  const { t, i18n } = useTranslation();
  const { hasPortfolio } = useMyPortfolio();
  const isKo = i18n.language === 'ko';

  const [followers, setFollowers] = useState<FollowerWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<'all' | 1 | 2 | 3>('all');
  const [sortBy, setSortBy] = useState<'startDate' | 'allocated' | 'pnl' | 'roi'>('startDate');

  const summary = useMemo(() => {
    const activeFollowers = followers.filter((f) => f.follow.status === 1).length;
    const totalAllocated = followers.reduce(
      (sum, f) => sum + Number(f.follow.allocatedAmount || 0),
      0
    );
    const totalProfitShared = followers.reduce(
      (sum, f) => sum + (f.stats?.totalProfitShared || 0),
      0
    );
    const avgRoi = followers.length > 0
      ? followers.reduce((sum, f) => sum + (f.stats?.roi || 0), 0) / followers.length
      : 0;

    return {
      total: followers.length,
      active: activeFollowers,
      totalAllocated,
      totalProfitShared,
      avgRoi,
    };
  }, [followers]);

  useEffect(() => {
    fetchFollowers();
  }, []);

  const fetchFollowers = async () => {
    setIsLoading(true);
    try {
      const response = await getMyFollowers();
      if (response.success) {
        const items = response.data?.items || [];
        const followersWithStats = items.map((item: any) => ({
          follow: {
            ...item,
            followerUsername: item.followerUsername || item.username || `User ${item.followerUserId}`,
            followerProfileImage: item.followerProfileImage || item.avatar || '',
          },
          stats: {
            totalTrades: 0,
            totalProfitShared: item.totalProfitShared || 0,
            roi: 0,
          },
        }));
        setFollowers(followersWithStats);
      }
    } catch (error) {
      console.error('[FollowersSection] fetchFollowers error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredFollowers = useMemo(() => {
    let result = [...followers];

    // 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter((f) => f.follow.status === statusFilter);
    }

    // 정렬
    result.sort((a, b) => {
      switch (sortBy) {
        case 'allocated':
          return Number(b.follow.allocatedAmount) - Number(a.follow.allocatedAmount);
        case 'pnl':
          return Number(b.follow.currentPnl) - Number(a.follow.currentPnl);
        case 'roi':
          return (b.stats?.roi || 0) - (a.stats?.roi || 0);
        case 'startDate':
        default:
          return new Date(b.follow.startedAt).getTime() - new Date(a.follow.startedAt).getTime();
      }
    });

    return result;
  }, [followers, statusFilter, sortBy]);

  // Not a leader
  if (!hasPortfolio) {
    return (
      <Container $isMobile={isMobile}>
        <EmptyStateCard>
          <EmptyIcon>👥</EmptyIcon>
          <EmptyTitle>{t('notALeader')}</EmptyTitle>
          <EmptyDescription>{t('becomeLeaderToSeeFollowers')}</EmptyDescription>
        </EmptyStateCard>
      </Container>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <Container $isMobile={isMobile}>
        <LoadingText>{t('loading')}</LoadingText>
      </Container>
    );
  }

  return (
    <Container $isMobile={isMobile}>
      {/* Header */}
      <SectionHeader $isMobile={isMobile}>
        <SectionTitle $isMobile={isMobile}>{t('myFollowers')}</SectionTitle>
      </SectionHeader>

      {/* Summary Stats */}
      <StatsCard>
        <StatItem>
          <StatLabel>{t('totalFollowers')}</StatLabel>
          <StatValue>{summary.total}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>{t('active')}</StatLabel>
          <StatValue $color="#07d993">{summary.active}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>{t('totalAllocated')}</StatLabel>
          <StatValue>
            {BigNumber(summary.totalAllocated).toFixed(2)}
            <StatUnit>USDT</StatUnit>
          </StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>{t('profitShared')}</StatLabel>
          <StatValue $color="#07d993">
            +{BigNumber(summary.totalProfitShared).toFixed(2)}
            <StatUnit>USDT</StatUnit>
          </StatValue>
        </StatItem>
      </StatsCard>

      {/* Filters */}
      <FiltersRow>
        <FilterButton
          $isActive={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
        >
          {t('all')}
        </FilterButton>
        <FilterButton
          $isActive={statusFilter === 1}
          onClick={() => setStatusFilter(1)}
        >
          {t('active')}
        </FilterButton>
        <FilterButton
          $isActive={statusFilter === 2}
          onClick={() => setStatusFilter(2)}
        >
          {t('paused')}
        </FilterButton>
        <SelectWrapper>
          <SelectDropdownGlobalStyle />
          <CSelect
            variant="default"
            value={sortBy}
            options={[
              { value: 'startDate', label: t('startDate') },
              { value: 'allocated', label: t('allocatedAmount') },
              { value: 'pnl', label: t('profitAmount') },
              { value: 'roi', label: t('profitRate') },
            ]}
            onChange={(value) => setSortBy(value as 'startDate' | 'allocated' | 'pnl' | 'roi')}
            getPopupContainer={() => document.body}
          />
        </SelectWrapper>
      </FiltersRow>

      {/* Followers List */}
      <FollowersListCard>
        <ListHeader>
          <ListTitle>
            {t('followersList')} ({filteredFollowers.length})
          </ListTitle>
          <RefreshButton onClick={fetchFollowers}>
            <RefreshIcon src="/assets/newHome/copy-trading/redo-icon.svg" alt="Refresh" />
            {t('refresh')}
          </RefreshButton>
        </ListHeader>

        {filteredFollowers.length === 0 ? (
          <EmptyList>
            <EmptyIcon>👥</EmptyIcon>
            <EmptyText>{t('noFollowersYet')}</EmptyText>
          </EmptyList>
        ) : (
          <FollowersList>
            {filteredFollowers.map((item) => (
              <FollowerCard key={item.follow.id}>
                <FollowerHeader>
                  <FollowerAvatar>
                    {item.follow.followerProfileImage ? (
                      <AvatarImage
                        src={`${import.meta.env.VITE_APP_API_MEDIA_DEV_URL}/${item.follow.followerProfileImage}`}
                        alt={item.follow.followerUsername}
                      />
                    ) : (
                      <AvatarFallback>
                        {(item.follow.followerUsername || 'U').charAt(0).toUpperCase()}
                      </AvatarFallback>
                    )}
                  </FollowerAvatar>
                  <FollowerInfo>
                    <FollowerName>{item.follow.followerUsername || t('unknown')}</FollowerName>
                    <FollowerDate>
                      {new Date(item.follow.startedAt).toLocaleDateString()}
                    </FollowerDate>
                  </FollowerInfo>
                  <StatusTag $status={item.follow.status}>
                    {item.follow.status === 1
                      ? t('active')
                      : item.follow.status === 2
                        ? t('paused')
                        : t('stopped')}
                  </StatusTag>
                </FollowerHeader>

                <FollowerStats>
                  <FollowerStatItem>
                    <FollowerStatLabel>{t('copyRatio')}</FollowerStatLabel>
                    <FollowerStatValue>
                      {BigNumber(item.follow.copyRatio || 0).multipliedBy(100).toFixed(0)}%
                    </FollowerStatValue>
                  </FollowerStatItem>
                  <FollowerStatItem>
                    <FollowerStatLabel>{t('allocated')}</FollowerStatLabel>
                    <FollowerStatValue>
                      {BigNumber(item.follow.allocatedAmount || 0).toFixed(2)} USDT
                    </FollowerStatValue>
                  </FollowerStatItem>
                  <FollowerStatItem>
                    <FollowerStatLabel>{t('currentPnl')}</FollowerStatLabel>
                    <FollowerStatValue
                      $color={Number(item.follow.currentPnl) >= 0 ? '#07d993' : '#ff4d4f'}
                    >
                      {Number(item.follow.currentPnl) >= 0 ? '+' : ''}
                      {BigNumber(item.follow.currentPnl || 0).toFixed(2)} USDT
                    </FollowerStatValue>
                  </FollowerStatItem>
                </FollowerStats>
              </FollowerCard>
            ))}
          </FollowersList>
        )}
      </FollowersListCard>
    </Container>
  );
};

// Styled Components
const Container = styled.section<{ $isMobile?: boolean }>`
  width: 100%;
  max-width: ${props => props.$isMobile ? '100%' : '1728px'};
  margin: 0 auto;
  margin-top: 32px;
  padding: ${props => props.$isMobile ? '0 16px' : '0 144px'};
  display: flex;
  flex-direction: column;
  gap: ${props => props.$isMobile ? '16px' : '32px'};
`;

const LoadingText = styled.div`
  width: 100%;
  padding: 60px 0;
  text-align: center;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
`;

const SectionHeader = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.$isMobile ? '16px' : '46px'};
`;

const SectionTitle = styled.h2<{ $isMobile?: boolean }>`
  font-family: 'Aggro', sans-serif;
  font-size: ${props => props.$isMobile ? '20px' : '28px'};
  font-weight: 500;
  color: #ffffff;
  letter-spacing: ${props => props.$isMobile ? '-0.4px' : '-0.56px'};
  line-height: ${props => props.$isMobile ? '28px' : '1.2'};
  flex-shrink: 0;
`;

const StatsCard = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 20px 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #b3b5bd;
`;

const StatValue = styled.div<{ $color?: string }>`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: ${props => props.$color || '#ffffff'};
`;

const StatUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: inherit;
  margin-bottom: 2px;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FilterButton = styled.button<{ $isActive: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: ${props => props.$isActive ? 700 : 400};
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${props => props.$isActive ? 'rgba(7, 217, 147, 0.15)' : 'rgba(91, 93, 107, 0.3)'};
  color: ${props => props.$isActive ? '#07d993' : '#b3b5bd'};
  border: none;

  &:hover {
    background: ${props => props.$isActive ? 'rgba(7, 217, 147, 0.2)' : 'rgba(91, 93, 107, 0.4)'};
  }
`;

const SelectWrapper = styled.div`
  margin-left: auto;
  min-width: 120px;

  .ant-select {
    width: 100%;
    height: 36px;
    background: rgba(91, 93, 107, 0.3);
    border-radius: 8px;
  }

  .ant-select-selector {
    background: transparent !important;
    border: none !important;
    height: 36px !important;
    padding: 0 12px !important;
  }

  .ant-select-selection-item {
    color: #b3b5bd;
    font-family: 'NanumSquareRound', sans-serif;
    font-size: 14px;
    line-height: 36px !important;
  }

  .ant-select-arrow {
    color: #b3b5bd;
  }

  .ant-select-focused .ant-select-selector {
    box-shadow: none !important;
  }
`;

const FollowersListCard = styled.div`
  background: #0c2323;
  border-radius: 16px;
  overflow: hidden;
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const ListTitle = styled.h3`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #b3b5bd;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const RefreshIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const FollowersList = styled.div`
  display: flex;
  flex-direction: column;
`;

const FollowerCard = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const FollowerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const FollowerAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #07d993 0%, #05a070 100%);
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const FollowerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FollowerName = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FollowerDate = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #777986;
`;

const StatusTag = styled.span<{ $status: number }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 700;
  background: ${props =>
    props.$status === 1 ? 'rgba(7, 217, 147, 0.2)' :
    props.$status === 2 ? 'rgba(255, 193, 7, 0.2)' :
    'rgba(255, 77, 79, 0.2)'
  };
  color: ${props =>
    props.$status === 1 ? '#07d993' :
    props.$status === 2 ? '#ffc107' :
    '#ff4d4f'
  };
`;

const FollowerStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const FollowerStatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FollowerStatLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 11px;
  color: #777986;
`;

const FollowerStatValue = styled.div<{ $color?: string }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.$color || '#ffffff'};
`;

const EmptyList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
`;

const EmptyText = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #777986;
  margin-top: 12px;
`;

const EmptyStateCard = styled.div`
  background: #0c2323;
  border-radius: 24px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
`;

const EmptyTitle = styled.h3`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const EmptyDescription = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #b3b5bd;
  margin: 0;
`;

export default FollowersSection;
